import type { Key, Selection } from 'react-aria-components'
import { useEffect, useMemo, useState } from 'react'
import { deepEqual, isNotNil } from '@/utils/object-util'
import { action, reaction } from 'mobx'

/**
 * Props to pass to {@link useViewModelBackedAriaSingleSelection}.
 */
export interface ViewModelBackedAriaSingleSelectionProps<
  T,
  DES extends boolean | undefined,
> {
  /**
   * The currently selected item.
   */
  selectedItem: T
  /**
   * Available items, used to locate the actual item based on the selected key.
   */
  items: readonly T[]
  /**
   * Whether to allow empty selection.
   * When `false` or unset, the {@link onSelectionChange} callback may be invoked with
   * `null`. When `true`, it will never be called with `null`.
   */
  disallowEmptySelection?: DES
  /**
   * Get the key for an item.
   * @param item
   */
  getKey: (item: DES extends true ? Exclude<T, null> : T) => Key | null
  /**
   * Called when the selection changes.
   *
   * @param selected
   */
  onSelectionChange: (
    selected: DES extends true ? Exclude<T, null> : T,
  ) => unknown
}

/**
 * Props to spread onto a React Aria Component which allows item selection.
 */
export interface ViewModelBackedAriaSingleSelectionResult {
  onSelectionChange: (selection: Selection) => unknown
  selectedKeys: Selection
  selectionMode: 'single'
  disallowEmptySelection: boolean
}

/**
 * Wires up a MobX view model to React Aria selection.
 */
export function useViewModelBackedAriaSingleSelection<
  T,
  DES extends boolean | undefined,
>({
  selectedItem,
  items,
  getKey,
  onSelectionChange,
  disallowEmptySelection,
}: ViewModelBackedAriaSingleSelectionProps<
  T,
  DES
>): ViewModelBackedAriaSingleSelectionResult {
  const [selectedKey, setSelectedKey] = useState<Key | null>(
    selectedItem ? getKey(selectedItem as Exclude<T, null>) : null,
  )

  useEffect(() => {
    const getCurrentKey = () =>
      selectedItem ? getKey(selectedItem as Exclude<T, null>) : null
    setSelectedKey(getCurrentKey())
    return reaction(getCurrentKey, setSelectedKey)
  }, [getKey, selectedItem])

  const selection = useMemo(
    () => (isNotNil(selectedKey) ? new Set([selectedKey]) : new Set<Key>()),
    [selectedKey],
  )

  const handleSelectionChange = useMemo(
    () =>
      action('onSelectionChange', (selection: Selection) => {
        if (!selection) {
          if (disallowEmptySelection) {
            onSelectionChange(null!)
          }
          return
        }

        if (items.length === 0) {
          return
        }

        if (selection === 'all') {
          // Select the first one, it's a single select.
          onSelectionChange(items[0] as Exclude<T, null>)
          return
        }

        if (selection.size === 0) {
          if (disallowEmptySelection) {
            onSelectionChange(null!)
          }
          return
        }

        const key = selection.values().next().value!
        const itemWithKey = items.find(
          (item) => item && getKey(item as Exclude<T, null>) === key,
        )
        if (!itemWithKey) {
          if (!disallowEmptySelection) {
            onSelectionChange(null!)
          }
          return
        }
        onSelectionChange(itemWithKey as Exclude<T, null>)
      }),
    [disallowEmptySelection, getKey, items, onSelectionChange],
  )

  return {
    selectionMode: 'single',
    selectedKeys: selection,
    onSelectionChange: handleSelectionChange,
    disallowEmptySelection: Boolean(disallowEmptySelection),
  }
}

/**
 * Props to pass to {@link useViewModelBackedAriaMultiSelection}.
 */
export interface ViewModelBackedAriaMultiSelectionProps<T> {
  /**
   * The currently selected items.
   */
  selectedItems: readonly T[]
  /**
   * Available items, used to locate the actual items based on the selected keys.
   */
  items: readonly T[]
  /**
   * Whether to allow empty selection.
   * When `false` or unset, the {@link onSelectionChange} callback may be
   * invoked with an empty array. When `true`, it will never be called with
   * an empty array.
   */
  disallowEmptySelection?: boolean
  /**
   * Get the key for an item.
   * @param item
   */
  getKey: (item: T) => Key | null
  /**
   * Called when the selection changes.
   *
   * @param selected
   */
  onSelectionChange: (selected: readonly T[]) => unknown
}

/**
 * Props to spread onto a React Aria Component which allows multiple item selection.
 */
export interface ViewModelBackedAriaMultiSelectionResult {
  onSelectionChange: (selection: Selection) => unknown
  selectedKeys: Selection
  selectionMode: 'multiple'
  disallowEmptySelection: boolean
}

/**
 * Wires up a MobX view model to React Aria multi-selection.
 */
export function useViewModelBackedAriaMultiSelection<T>({
  selectedItems,
  items,
  getKey,
  onSelectionChange,
  disallowEmptySelection,
}: ViewModelBackedAriaMultiSelectionProps<T>): ViewModelBackedAriaMultiSelectionResult {
  const [selectedKeys, setSelectedKeys] = useState<Selection>(() =>
    getKeys(selectedItems, getKey),
  )

  useEffect(() => {
    const getCurrentKeys = () => getKeys(selectedItems, getKey)
    const keys = getCurrentKeys()
    if (!deepEqual(keys, selectedKeys)) {
      setSelectedKeys(keys)
    }

    return reaction(getCurrentKeys, setSelectedKeys)
  }, [getKey, selectedKeys, selectedItems])

  const handleSelectionChange = useMemo(
    () =>
      action('onSelectionChange', (selection: Selection) => {
        if (!selection) {
          if (!disallowEmptySelection) {
            onSelectionChange([])
          }
          return
        }

        if (items.length === 0) {
          return
        }

        if (selection === 'all') {
          onSelectionChange(items)
          return
        }

        if (selection.size === 0) {
          if (!disallowEmptySelection) {
            onSelectionChange([])
          }
          return
        }

        const selectedItemsList: T[] = []
        for (const key of selection) {
          const itemWithKey = items.find((item) => getKey(item) === key)
          if (itemWithKey) {
            selectedItemsList.push(itemWithKey)
          }
        }

        if (selectedItemsList.length === 0 && disallowEmptySelection) {
          return
        }

        onSelectionChange(selectedItemsList)
      }),
    [disallowEmptySelection, getKey, items, onSelectionChange],
  )

  return {
    selectionMode: 'multiple',
    selectedKeys,
    onSelectionChange: handleSelectionChange,
    disallowEmptySelection: Boolean(disallowEmptySelection),
  }
}

/**
 * Helper for getting keys for multi-selection.
 *
 * @param selectedItems
 * @param getKey
 */
function getKeys<T>(
  selectedItems: readonly T[],
  getKey: (item: T) => Key | null,
): Set<Key> {
  const keys = new Set<Key>()
  for (const item of selectedItems) {
    const key = getKey(item)
    if (isNotNil(key)) {
      keys.add(key)
    }
  }
  return keys
}
